<template>
  <div class="page-wrapper tc-calendar">
    <div class="content container-fluid">

      <!-- Header -->
      <div class="tc-hdr">
        <div class="tc-title">Lịch thu chi</div>
        <div class="tc-subtitle">Xem tổng thu chi theo ngày</div>
      </div>

      <!-- Điều khiển tháng -->
      <div class="tc-month-card">
        <button type="button" class="btn btn-sm btn-link tc-month-btn" @click="prevMonth">
          <i class="la la-angle-left"></i>
        </button>
        <div class="tc-month-label">
          {{ monthLabel }}
        </div>
        <button type="button" class="btn btn-sm btn-link tc-month-btn" @click="nextMonth">
          <i class="la la-angle-right"></i>
        </button>
      </div>

      <!-- Ghi chú màu -->
      <div class="tc-legend">
        <span><span class="dot chi"></span> Chi</span>
        <span><span class="dot thu"></span> Thu</span>
      </div>

      <!-- Lịch -->
      <div class="tc-calendar-card">
        <div class="tc-week-header">
          <div v-for="w in weekdays" :key="w" class="tc-weekday">
            {{ w }}
          </div>
        </div>

        <div v-if="loading" class="text-center text-muted py-3">
          Đang tải dữ liệu...
        </div>

        <div v-else class="tc-days-grid">
          <div
            v-for="cell in calendarCells"
            :key="cell.key"
            class="tc-day-cell"
            :class="{
              'other-month': !cell.inMonth,
              'today': cell.isToday,
              'has-data': cell.summary && cell.summary.soPhieu > 0
            }"
            @click="openDayDetail(cell)"
          >
            <div class="tc-day-number">
              {{ cell.dayNumber || '' }}
            </div>

            <div v-if="cell.summary && cell.summary.soPhieu > 0" class="tc-day-summary">
              <div
                v-if="cell.summary.tongChi > 0"
                class="chi-amount"
              >
                -{{ formatMoney(cell.summary.tongChi) }}
              </div>
              <div
                v-if="cell.summary.tongThu > 0"
                class="thu-amount"
              >
                +{{ formatMoney(cell.summary.tongThu) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal chi tiết 1 ngày -->
      <div v-if="showDetail" class="modal-backdrop fade show"></div>
      <div v-if="showDetail" class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                Giao dịch ngày {{ detailDateLabel }}
              </h5>
              <button type="button" class="close" @click="closeDetail">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div v-if="loadingDetail" class="text-muted text-center py-3">
                Đang tải chi tiết...
              </div>
              <div v-else-if="!detailList.length" class="text-muted text-center py-3">
                Không có giao dịch nào.
              </div>
              <div
                v-else
                v-for="item in detailList"
                :key="item.id"
                class="tc-detail-item"
              >
                <div class="tc-detail-left">
                  <div class="tc-detail-title">
                    {{ item.tenDanhMuc }}
                  </div>
                  <div class="tc-detail-sub">
                    <span v-if="item.ghiChu">{{ item.ghiChu }}</span>
                    <span v-else class="text-muted">Không ghi chú</span>
                  </div>
                </div>
                <div class="tc-detail-right">
                  <div
                    class="tc-detail-money"
                    :class="{ chi: item.loai === 'CHI', thu: item.loai === 'THU' }"
                  >
                    {{ item.loai === 'CHI' ? '-' : '+' }}
                    {{ formatMoney(item.soTien) }}
                  </div>
                  <div class="tc-detail-status" :class="statusClass(item.trangThai)">
                    {{ statusLabel(item.trangThai) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeDetail">
                Đóng
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/api/index.js"
import { useToast } from "vue-toastification"

const toast = useToast()

const currentYear = ref(new Date().getFullYear())
const currentMonth = ref(new Date().getMonth() + 1)

const weekdays = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"]

const loading = ref(false)

// yyyy-MM-dd -> { tongChi, tongThu, soPhieu }
const dailyMap = ref({})

const showDetail = ref(false)
const detailList = ref([])
const detailDateLabel = ref("")
const loadingDetail = ref(false)

const monthLabel = computed(() => {
  return `Tháng ${currentMonth.value}/${currentYear.value}`
})

const calendarCells = computed(() => {
  const year = currentYear.value
  const monthIndex = currentMonth.value - 1

  const first = new Date(year, monthIndex, 1)
  const last = new Date(year, monthIndex + 1, 0)
  const firstWeekday = first.getDay()
  const totalDays = last.getDate()

  const cells = []
  const todayStr = toISODate(new Date())

  for (let i = 0; i < firstWeekday; i++) {
    cells.push({
      key: `blank-${i}`,
      inMonth: false,
      dayNumber: "",
      dateStr: null,
      isToday: false,
      summary: null
    })
  }

  for (let d = 1; d <= totalDays; d++) {
    const dt = new Date(year, monthIndex, d)
    const dateStr = toISODate(dt)
    const summary = dailyMap.value[dateStr] || null

    cells.push({
      key: dateStr,
      inMonth: true,
      dayNumber: d,
      dateStr,
      isToday: dateStr === todayStr,
      summary
    })
  }
  return cells
})

function toISODate(d) {
  const off = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
  return off.toISOString().slice(0, 10)
}

function pad2(n) {
  return n < 10 ? "0" + n : "" + n
}

function formatMoney(v) {
  if (v == null) return "0"
  const num = typeof v === "number" ? v : Number(v)
  if (Number.isNaN(num)) return "0"
  return num.toLocaleString("vi-VN")
}

function formatDateLabel(str) {
  if (!str) return ""
  const d = new Date(str + "T00:00:00")
  const dd = pad2(d.getDate())
  const mm = pad2(d.getMonth() + 1)
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

function statusLabel(st) {
  switch (st) {
    case "CHO_DUYET": return "Chờ duyệt"
    case "DA_DUYET": return "Đã duyệt"
    case "DA_THANH_TOAN": return "Đã thanh toán"
    case "DA_QUYET_TOAN": return "Đã quyết toán"
    case "TU_CHOI": return "Từ chối"
    default: return st || ""
  }
}

function statusClass(st) {
  return {
    "st-cho-duyet": st === "CHO_DUYET",
    "st-da-duyet": st === "DA_DUYET",
    "st-da-thanh-toan": st === "DA_THANH_TOAN",
    "st-da-quyet-toan": st === "DA_QUYET_TOAN",
    "st-tu-choi": st === "TU_CHOI"
  }
}

// ===== load summary-by-day cho tháng hiện tại =====
async function loadMonthData() {
  loading.value = true
  dailyMap.value = {}

  try {
    const res = await api.get("/api/thu-chi/my/summary-by-day", {
      params: {
        thang: currentMonth.value,
        nam: currentYear.value
      }
    })
    const arr = res.data || []
    const map = {}
    arr.forEach(row => {
      const d = row.ngay
      if (!d) return
      map[d] = {
        tongChi: Number(row.tongChi || 0),
        tongThu: Number(row.tongThu || 0),
        soPhieu: Number(row.soPhieu || 0)
      }
    })
    dailyMap.value = map
  } catch (e) {
    console.error(e)
    toast.error("Không tải được dữ liệu lịch thu chi")
  } finally {
    loading.value = false
  }
}

function prevMonth() {
  let y = currentYear.value
  let m = currentMonth.value - 1
  if (m < 1) {
    m = 12
    y -= 1
  }
  currentYear.value = y
  currentMonth.value = m
  loadMonthData()
}

function nextMonth() {
  let y = currentYear.value
  let m = currentMonth.value + 1
  if (m > 12) {
    m = 1
    y += 1
  }
  currentYear.value = y
  currentMonth.value = m
  loadMonthData()
}

// ===== modal chi tiết ngày =====
async function openDayDetail(cell) {
  if (!cell || !cell.inMonth || !cell.dateStr) return
  const summary = cell.summary
  if (!summary || summary.soPhieu <= 0) return

  showDetail.value = true
  detailDateLabel.value = formatDateLabel(cell.dateStr)
  detailList.value = []
  loadingDetail.value = true

  try {
    const res = await api.get("/api/thu-chi/my", {
      params: {
        tuNgay: cell.dateStr,
        denNgay: cell.dateStr,
        page: 0,
        size: 200
      }
    })
    const pageData = res.data || {}
    const list = pageData.content || pageData || []
    detailList.value = list
  } catch (e) {
    console.error(e)
    toast.error("Không tải được chi tiết giao dịch ngày")
    detailList.value = []
  } finally {
    loadingDetail.value = false
  }
}

function closeDetail() {
  showDetail.value = false
  detailList.value = []
  detailDateLabel.value = ""
  loadingDetail.value = false
}

onMounted(() => {
  loadMonthData()
})
</script>

<style scoped>
.tc-calendar {
  background: #f6f8fb;
  min-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
}

.tc-calendar .content {
  max-width: 480px;
}

@media (min-width: 992px) {
  .tc-calendar .content {
    max-width: 720px;
  }
}

/* header */
.tc-hdr {
  padding: 4px 2px 4px;
}

.tc-title {
  font-weight: 600;
  font-size: 18px;
}

.tc-subtitle {
  font-size: 12px;
  color: #888;
}

/* month control */
.tc-month-card {
  margin-top: 4px;
  background: #fff;
  border-radius: 12px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0,0,0,0.03);
}

.tc-month-label {
  font-weight: 600;
  font-size: 15px;
}

.tc-month-btn {
  color: #ff8b00;
}

/* legend */
.tc-legend {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  font-size: 11px;
  color: #666;
  margin-top: 4px;
}

.tc-legend .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}

.tc-legend .dot.chi {
  background: #e53935;
}

.tc-legend .dot.thu {
  background: #43a047;
}

/* calendar card */
.tc-calendar-card {
  background: #fff;
  border-radius: 12px;
  margin-top: 6px;
  padding: 6px 4px 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.03);
}

.tc-week-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}

.tc-weekday {
  text-align: center;
  font-size: 11px;
  color: #999;
}

/* days grid */
.tc-days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.tc-day-cell {
  min-height: 56px;
  padding: 2px 3px;
  border-radius: 6px;
  position: relative;
  cursor: pointer;
}

.tc-day-cell.other-month {
  background: #fafafa;
  color: #ccc;
}

.tc-day-cell.today {
  border: 1px solid #ff8b00;
}

.tc-day-cell.has-data {
  background: #fffdf5;
}

.tc-day-number {
  font-size: 11px;
  font-weight: 600;
  color: #555;
}

.tc-day-summary {
  margin-top: 4px;
  font-size: 10px;
  line-height: 1.2;
}

.tc-day-summary .chi-amount {
  color: #e53935;
}

.tc-day-summary .thu-amount {
  color: #43a047;
}

/* detail modal */
.tc-detail-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.tc-detail-item:last-child {
  border-bottom: none;
}

.tc-detail-left {
  max-width: 60%;
}

.tc-detail-title {
  font-size: 13px;
  font-weight: 600;
}

.tc-detail-sub {
  font-size: 11px;
  color: #777;
}

.tc-detail-right {
  text-align: right;
}

.tc-detail-money {
  font-size: 13px;
  font-weight: 600;
}

.tc-detail-money.chi {
  color: #e53935;
}

.tc-detail-money.thu {
  color: #43a047;
}

.tc-detail-status {
  margin-top: 2px;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  display: inline-block;
}

.st-cho-duyet {
  background: #fff3cd;
  color: #856404;
}

.st-da-duyet {
  background: #e3f2fd;
  color: #1565c0;
}

.st-da-thanh-toan {
  background: #e8f5e9;
  color: #2e7d32;
}

.st-da-quyet-toan {
  background: #ede7f6;
  color: #5e35b1;
}

.st-tu-choi {
  background: #ffebee;
  color: #c62828;
}

.tc-calendar {
  background: #f6f8fb;
  min-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
}

.tc-calendar .content {
  width: 100%;
  max-width: 480px;
}

/* Tablet */
@media (min-width: 768px) {
  .tc-calendar .content {
    max-width: 720px;
  }

  .tc-days-grid {
    gap: 4px;
  }

  .tc-day-cell {
    min-height: 70px;
    padding: 4px;
  }
}

/* Desktop */
@media (min-width: 1200px) {
  .tc-calendar .content {
    max-width: 800px;
  }

  .tc-days-grid {
    gap: 6px;
  }

  .tc-day-cell {
    min-height: 80px;
    padding: 6px;
  }

  .tc-day-cell:hover {
    background-color: #f0f0f0;
  }
}

/* Mobile nhỏ */
@media (max-width: 375px) {
  .tc-day-cell {
    min-height: 50px;
    padding: 2px;
  }

  .tc-day-summary {
    font-size: 9px;
  }
}
</style>
